import { z } from 'zod';

import { ApiError } from '@/lib/api';
import { parsePage, type PageResult } from '@/lib/query/page';
import { staffSession } from '@/lib/session';

export const ACCOUNT_NAME_MAX = 120;
export const ACCOUNT_EMAIL_MAX = 191;
export const ACCOUNT_PASSWORD_MIN = 12;
export const ACCOUNT_PASSWORD_MAX = 128;

const platformAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  scope: z.enum(['platform', 'agency']),
  active: z.boolean(),
  agencyId: z.number().nullable(),
  createdAt: z.string().nullable(),
});

export type PlatformAccount = z.infer<typeof platformAccountSchema>;

const staffInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.string(),
  expiresAt: z.string().nullable().optional(),
});

export type StaffInvitationRecord = z.infer<typeof staffInvitationSchema>;

const agencyDirectoryEntrySchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  staff: z.array(platformAccountSchema),
  invitations: z.array(staffInvitationSchema).optional().default([]),
  error: z.string().nullable(),
});

export type AgencyDirectoryEntry = z.infer<typeof agencyDirectoryEntrySchema>;

const platformAccountListSchema = z.array(platformAccountSchema);
const agencyDirectorySchema = z.array(agencyDirectoryEntrySchema);

export function parsePlatformAccounts(payload: unknown): PlatformAccount[] {
  const parsed = platformAccountListSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The account list could not be read.');
  return parsed.data;
}

export function parsePlatformAccountPage(payload: unknown): PageResult<PlatformAccount> {
  return parsePage(payload, platformAccountSchema, 'The account list');
}

export function parseStaffInvitation(payload: unknown): StaffInvitationRecord {
  const parsed = staffInvitationSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The invitation could not be read.');
  return parsed.data;
}

export function parsePlatformAccount(payload: unknown): PlatformAccount {
  const parsed = platformAccountSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The account could not be read.');
  return parsed.data;
}

export function parseAgencyDirectory(payload: unknown): AgencyDirectoryEntry[] {
  const parsed = agencyDirectorySchema.safeParse(payload);
  if (!parsed.success) throw new Error('The staff directory could not be read.');
  return parsed.data;
}

export function sortAccountsByName(rows: PlatformAccount[]): PlatformAccount[] {
  return [...rows].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));
}

export function agenciesOpenForAccounts(rows: AgencyDirectoryEntry[]): AgencyDirectoryEntry[] {
  return rows.filter((agency) => agency.error === null);
}

export function upsertAgencyMember(
  rows: AgencyDirectoryEntry[],
  slug: string,
  member: PlatformAccount,
  mode: 'create' | 'replace',
): AgencyDirectoryEntry[] {
  return rows.map((agency) => {
    if (agency.slug !== slug) return agency;
    const staff = mode === 'create'
      ? [member, ...agency.staff]
      : agency.staff.map((current) => (current.id === member.id ? member : current));
    return { ...agency, staff };
  });
}

export function filterAgencyDirectory(rows: AgencyDirectoryEntry[], query: string): AgencyDirectoryEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;

  const matched: AgencyDirectoryEntry[] = [];
  for (const agency of rows) {
    const agencyMatches = agency.name.toLowerCase().includes(needle) || agency.slug.toLowerCase().includes(needle);
    if (agencyMatches) {
      matched.push(agency);
      continue;
    }
    const staff = agency.staff.filter((member) => member.email.toLowerCase().includes(needle) || member.name.toLowerCase().includes(needle));
    const invitations = agency.invitations.filter((invite) => invite.email.toLowerCase().includes(needle) || invite.name.toLowerCase().includes(needle));
    if (staff.length > 0 || invitations.length > 0) matched.push({ ...agency, staff, invitations });
  }
  return matched;
}

export function accountFieldError(input: { name: string; email: string; password: string; passwordRequired: boolean }): string | null {
  const name = input.name.trim();
  const email = input.email.trim();
  if (name.length < 1 || name.length > ACCOUNT_NAME_MAX) return `Name must be between 1 and ${ACCOUNT_NAME_MAX} characters.`;
  if (email.length > ACCOUNT_EMAIL_MAX || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
  const passwordNeeded = input.passwordRequired || input.password !== '';
  if (!passwordNeeded) return null;
  if (input.password.length < ACCOUNT_PASSWORD_MIN || input.password.length > ACCOUNT_PASSWORD_MAX) {
    return `Password must be between ${ACCOUNT_PASSWORD_MIN} and ${ACCOUNT_PASSWORD_MAX} characters.`;
  }
  return null;
}

export function leaveIfSignedOut(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const code = error.body !== null && typeof error.body === 'object'
    ? (error.body as { code?: unknown }).code
    : null;
  const needsTwoFactor = error.status === 403 && code === 'two_factor_required';
  if (error.status !== 401 && !needsTwoFactor) return false;
  staffSession.set(null);
  window.location.assign('/login');
  return true;
}

export function accountError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message.trim() !== '') return error.message;
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return fallback;
}
